import { useState, useCallback } from 'react';
import { debugWarn, debugError } from '../../../../utils/debugLog';
import {
  fetchWithDeduplication,
  transformStatsToTemplatePerformance,
  transformCostBreakdownToCostComparison,
  transformCostBreakdownToModelUsage,
  transformStatsToCategoryAnalytics,
  transformStatsToTopPerformers,
} from '../useDashboardDataUtils';

/**
 * useSecondaryData Hook
 * Manages secondary dashboard data (analytics, performance, charts)
 */
export const useSecondaryData = (
  queryString,
  requestKeySuffix,
  isAllTime,
  isMountedRef,
  setCostBreakdown,
  costBreakdown, // Reuse cost-breakdown from core data to avoid duplicate fetch
  templateGridPage = 1,
  templateGridPageSize = 10,
  templateGridCategory = null,
  templateGridHideZeroActivity = false,
  templateGridSearch = '',
  templateGridSortBy = 'last_activity',
  templateGridSortOrder = 'desc'
) => {
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [secondaryError, setSecondaryError] = useState(null);
  const [templatePerformance, setTemplatePerformance] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [categoryAnalytics, setCategoryAnalytics] = useState(null);
  const [modelUsageDistribution, setModelUsageDistribution] = useState(null);
  const [categoryAnalyticsData, setCategoryAnalyticsData] = useState(null);
  const [adapters, setAdapters] = useState(null);
  const [executionsPerWeek, setExecutionsPerWeek] = useState(null);
  const [providerDistribution, setProviderDistribution] = useState(null);
  const [topModels, setTopModels] = useState(null);
  const [costComparison, setCostComparison] = useState(null);

  const fetchSecondaryData = useCallback(async () => {
    if (!isMountedRef.current) return;

    setSecondaryLoading(true);
    setSecondaryError(null);

    try {
      let templatePerformanceData, topPerformersData, categoryAnalyticsData;
      let modelUsageData,
        providerDistributionData,
        topModelsData,
        costComparisonData;

      if (isAllTime) {
        // All-time: Use execution-based endpoints
        const allTimeStartDate = '2000-01-01T00:00:00Z';
        const allTimeEndDate = new Date().toISOString();

        // Build template grid URL with pagination and filters
        const gridParams = new URLSearchParams({
          page: templateGridPage.toString(),
          page_size: templateGridPageSize.toString(),
        });
        if (templateGridCategory && templateGridCategory !== 'all') {
          gridParams.append('category', templateGridCategory);
        }
        if (templateGridHideZeroActivity) {
          gridParams.append('hide_zero_activity', 'true');
        }
        // Only search if 3+ characters (minimum requirement)
        if (templateGridSearch && templateGridSearch.trim().length >= 3) {
          gridParams.append('search', templateGridSearch.trim());
        }
        gridParams.append('sort_by', templateGridSortBy);
        gridParams.append('sort_order', templateGridSortOrder);
        const gridUrl = `/api/v1/templates/analytics/dashboard/templates/grid?${gridParams.toString()}`;
        const gridCacheKey = `template-grid-all-time-page-${templateGridPage}-size-${templateGridPageSize}-cat-${templateGridCategory || 'all'}-hideZero-${templateGridHideZeroActivity}-search-${templateGridSearch || 'none'}-sort-${templateGridSortBy}-${templateGridSortOrder}`;

        // Use Promise.allSettled to handle partial failures gracefully
        // NOTE: cost-breakdown is already fetched in useCoreData, so we reuse it here
        const [
          templateGridResult,
          providerDistributionResult,
          topModelsResult,
        ] = await Promise.allSettled([
          // Use new template grid endpoint with server-side pagination
          fetchWithDeduplication(gridUrl, gridCacheKey),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/provider-distribution?start_date=${encodeURIComponent(allTimeStartDate)}&end_date=${encodeURIComponent(allTimeEndDate)}`,
            `provider-distribution-all-time`
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/top-models?start_date=${encodeURIComponent(allTimeStartDate)}&end_date=${encodeURIComponent(allTimeEndDate)}&limit=5`,
            `top-models-all-time`
          ),
        ]);

        // Extract responses from settled promises
        const templateGridResponse =
          templateGridResult.status === 'fulfilled'
            ? templateGridResult.value
            : { success: false, error: templateGridResult.reason?.message };

        // Reuse cost-breakdown from core data instead of fetching again
        const costBreakdownResponseAllTime = costBreakdown || {
          success: false,
          models: [],
        };
        const providerDistributionResponseAllTime =
          providerDistributionResult.status === 'fulfilled'
            ? providerDistributionResult.value
            : {
                success: false,
                error: providerDistributionResult.reason?.message,
              };
        const topModelsResponseAllTime =
          topModelsResult.status === 'fulfilled'
            ? topModelsResult.value
            : { success: false, error: topModelsResult.reason?.message };

        // Log errors for debugging
        if (providerDistributionResult.status === 'rejected') {
          debugWarn(
            'Failed to fetch all-time provider distribution:',
            providerDistributionResult.reason
          );
        }
        if (topModelsResult.status === 'rejected') {
          debugWarn(
            'Failed to fetch all-time top models:',
            topModelsResult.reason
          );
        }

        if (templateGridResponse?.success) {
          // Transform template grid response to match expected format
          // Include pagination metadata for server-side pagination
          const gridAsStatsFormat = {
            success: true,
            templates: templateGridResponse.templates || [],
            total_count: templateGridResponse.pagination?.total_count || 0,
            pagination: templateGridResponse.pagination || null,
          };
          templatePerformanceData =
            transformStatsToTemplatePerformance(gridAsStatsFormat);
          // Store pagination metadata in templatePerformanceData
          if (templatePerformanceData) {
            templatePerformanceData.pagination =
              templateGridResponse.pagination;
          }
          categoryAnalyticsData =
            transformStatsToCategoryAnalytics(gridAsStatsFormat);
          topPerformersData = transformStatsToTopPerformers(gridAsStatsFormat);
        }

        if (costBreakdownResponseAllTime?.success) {
          const costBreakdownModels = costBreakdownResponseAllTime.models || [];
          // Note: costBreakdown state is already set by useCoreData, no need to set it again

          // Only transform if we have valid data
          if (costBreakdownModels && costBreakdownModels.length > 0) {
            costComparisonData =
              transformCostBreakdownToCostComparison(costBreakdownModels);
            modelUsageData =
              transformCostBreakdownToModelUsage(costBreakdownModels);
          } else {
            // Set empty data structure if no models
            costComparisonData = {
              success: true,
              cost_comparison: [],
              total_executions: 0,
              period: 'All Time',
            };
            modelUsageData = {
              success: true,
              model_distribution: [],
              total_executions: 0,
              period: 'All Time',
            };
          }
        } else {
          // If cost breakdown failed, set empty structures
          costComparisonData = {
            success: false,
            cost_comparison: [],
            total_executions: 0,
            period: 'All Time',
          };
          modelUsageData = {
            success: false,
            model_distribution: [],
            total_executions: 0,
            period: 'All Time',
          };
        }

        // Validate provider distribution response
        if (
          providerDistributionResponseAllTime?.success &&
          providerDistributionResponseAllTime.provider_distribution &&
          Array.isArray(
            providerDistributionResponseAllTime.provider_distribution
          ) &&
          providerDistributionResponseAllTime.provider_distribution.length > 0
        ) {
          providerDistributionData = {
            success: true,
            provider_distribution:
              providerDistributionResponseAllTime.provider_distribution,
            total_executions:
              providerDistributionResponseAllTime.total_executions || 0,
            period: 'All Time',
          };
        } else {
          // Log warning if response is invalid
          if (providerDistributionResponseAllTime) {
            debugWarn(
              'Provider distribution response invalid:',
              providerDistributionResponseAllTime
            );
          }
          providerDistributionData = null;
        }

        // Validate top models response
        if (
          topModelsResponseAllTime?.success &&
          topModelsResponseAllTime.top_models &&
          Array.isArray(topModelsResponseAllTime.top_models) &&
          topModelsResponseAllTime.top_models.length > 0
        ) {
          topModelsData = {
            success: true,
            top_models: topModelsResponseAllTime.top_models,
            total_executions: topModelsResponseAllTime.total_executions || 0,
            period: 'All Time',
          };
        } else {
          // Log warning if response is invalid
          if (topModelsResponseAllTime) {
            debugWarn('Top models response invalid:', topModelsResponseAllTime);
          }
          topModelsData = null;
        }
      } else {
        // Time-filtered: use legacy endpoints
        [templatePerformanceData, topPerformersData, categoryAnalyticsData] =
          await Promise.all([
            fetchWithDeduplication(
              `/api/v1/templates/analytics/dashboard/template-performance?${queryString}`,
              `template-performance-${requestKeySuffix}`
            ),
            fetchWithDeduplication(
              `/api/v1/templates/analytics/dashboard/top-performers?${queryString}`,
              `top-performers-${requestKeySuffix}`
            ),
            fetchWithDeduplication(
              `/api/v1/templates/analytics/dashboard/category-analytics?${queryString}`,
              `category-analytics-${requestKeySuffix}`
            ),
          ]);
      }

      // Load chart data
      let executionsPerWeekData, adaptersData;

      if (isAllTime) {
        const [executionsPerWeekResult, adaptersResult] =
          await Promise.allSettled([
            fetchWithDeduplication(
              `/api/v1/templates/analytics/dashboard/executions-per-week?days=30`,
              `executions-per-week-30d`
            ),
            fetchWithDeduplication(
              '/api/v1/templates/analytics/adapters',
              'adapters'
            ),
          ]);

        executionsPerWeekData =
          executionsPerWeekResult.status === 'fulfilled'
            ? executionsPerWeekResult.value || {
                success: false,
                error: 'Empty response from API',
              }
            : {
                success: false,
                error:
                  executionsPerWeekResult.reason?.message || 'Unknown error',
              };

        adaptersData =
          adaptersResult.status === 'fulfilled'
            ? adaptersResult.value
            : {
                success: false,
                error: adaptersResult.reason?.message || 'Unknown error',
              };

        if (executionsPerWeekResult.status === 'rejected') {
          debugWarn(
            'Failed to load executions-per-week:',
            executionsPerWeekResult.reason
          );
        } else if (executionsPerWeekResult.status === 'fulfilled') {
          // Log successful response for debugging
          const response = executionsPerWeekResult.value;
          if (!response || !response.success) {
            debugWarn(
              'Executions-per-week response missing success flag or data:',
              response
            );
          } else if (
            !response.weekly_executions ||
            !Array.isArray(response.weekly_executions)
          ) {
            debugWarn(
              'Executions-per-week response missing weekly_executions array:',
              response
            );
          }
        }
        if (adaptersResult.status === 'rejected') {
          debugWarn('Failed to load adapters:', adaptersResult.reason);
        }
      } else {
        // Time-filtered: load all charts
        const [
          modelUsageResult,
          adaptersResult,
          executionsPerWeekResult,
          providerDistributionResult,
          topModelsResult,
          costComparisonResult,
        ] = await Promise.allSettled([
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/model-usage-distribution?${queryString}`,
            `model-usage-${requestKeySuffix}`
          ),
          fetchWithDeduplication(
            '/api/v1/templates/analytics/adapters',
            'adapters'
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/executions-per-week?${queryString}`,
            `executions-per-week-${requestKeySuffix}`
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/provider-distribution?${queryString}`,
            `provider-distribution-${requestKeySuffix}`
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/top-models?${queryString}&limit=5`,
            `top-models-${requestKeySuffix}`
          ),
          fetchWithDeduplication(
            `/api/v1/templates/analytics/dashboard/cost-comparison?${queryString}`,
            `cost-comparison-${requestKeySuffix}`
          ),
        ]);

        modelUsageData =
          modelUsageResult.status === 'fulfilled'
            ? modelUsageResult.value
            : {
                success: false,
                error: modelUsageResult.reason?.message || 'Unknown error',
              };

        adaptersData =
          adaptersResult.status === 'fulfilled'
            ? adaptersResult.value
            : {
                success: false,
                error: adaptersResult.reason?.message || 'Unknown error',
              };

        executionsPerWeekData =
          executionsPerWeekResult.status === 'fulfilled'
            ? executionsPerWeekResult.value || {
                success: false,
                error: 'Empty response from API',
              }
            : {
                success: false,
                error:
                  executionsPerWeekResult.reason?.message || 'Unknown error',
              };

        providerDistributionData =
          providerDistributionResult.status === 'fulfilled'
            ? providerDistributionResult.value
            : {
                success: false,
                error:
                  providerDistributionResult.reason?.message || 'Unknown error',
              };

        topModelsData =
          topModelsResult.status === 'fulfilled'
            ? topModelsResult.value
            : {
                success: false,
                error: topModelsResult.reason?.message || 'Unknown error',
              };

        costComparisonData =
          costComparisonResult.status === 'fulfilled'
            ? costComparisonResult.value
            : {
                success: false,
                error: costComparisonResult.reason?.message || 'Unknown error',
              };

        const failedEndpoints = [
          { name: 'model-usage-distribution', result: modelUsageResult },
          { name: 'adapters', result: adaptersResult },
          { name: 'executions-per-week', result: executionsPerWeekResult },
          { name: 'provider-distribution', result: providerDistributionResult },
          { name: 'top-models', result: topModelsResult },
          { name: 'cost-comparison', result: costComparisonResult },
        ];

        failedEndpoints.forEach(({ name, result }) => {
          if (result.status === 'rejected') {
            debugWarn(`Failed to load ${name}:`, result.reason);
          } else if (
            result.status === 'fulfilled' &&
            name === 'executions-per-week'
          ) {
            // Log successful response for debugging
            const response = result.value;
            if (!response || !response.success) {
              debugWarn(
                `Executions-per-week response missing success flag or data:`,
                response
              );
            } else if (
              !response.weekly_executions ||
              !Array.isArray(response.weekly_executions)
            ) {
              debugWarn(
                `Executions-per-week response missing weekly_executions array:`,
                response
              );
            }
          }
        });
      }

      // Update secondary data state
      if (isMountedRef.current) {
        setTemplatePerformance(
          templatePerformanceData &&
            (templatePerformanceData.success ||
              templatePerformanceData.templates)
            ? templatePerformanceData
            : null
        );
        setTopPerformers(topPerformersData?.success ? topPerformersData : null);
        setModelUsageDistribution(
          modelUsageData?.success ? modelUsageData : null
        );
        setCategoryAnalytics(
          categoryAnalyticsData?.success
            ? categoryAnalyticsData?.category_analytics || []
            : null
        );
        setCategoryAnalyticsData(
          categoryAnalyticsData?.success ? categoryAnalyticsData : null
        );
        setAdapters(adaptersData?.success ? adaptersData : null);
        // Set executionsPerWeek if we have valid data (check for success flag OR weekly_executions array)
        setExecutionsPerWeek(
          executionsPerWeekData &&
            (executionsPerWeekData.success ||
              (executionsPerWeekData.weekly_executions &&
                Array.isArray(executionsPerWeekData.weekly_executions)))
            ? executionsPerWeekData
            : null
        );
        setProviderDistribution(
          providerDistributionData?.success ? providerDistributionData : null
        );
        setTopModels(topModelsData?.success ? topModelsData : null);
        // Set cost comparison - ensure it has valid structure even if empty
        setCostComparison(
          costComparisonData &&
            (costComparisonData.success ||
              (costComparisonData.cost_comparison &&
                Array.isArray(costComparisonData.cost_comparison)))
            ? costComparisonData
            : null
        );

        // Only set error if ALL critical endpoints failed
        const hasAnyCriticalData =
          (templatePerformanceData &&
            (templatePerformanceData.success ||
              templatePerformanceData.templates)) ||
          topPerformersData?.success ||
          categoryAnalyticsData?.success;

        if (
          !hasAnyCriticalData &&
          (templatePerformanceData === null ||
            topPerformersData === null ||
            categoryAnalyticsData === null)
        ) {
          const failedEndpoints = [];
          if (!templatePerformanceData || !templatePerformanceData.success)
            failedEndpoints.push('template-performance');
          if (!topPerformersData?.success)
            failedEndpoints.push('top-performers');
          if (!categoryAnalyticsData?.success)
            failedEndpoints.push('category-analytics');
          if (failedEndpoints.length > 0) {
            setSecondaryError(
              `Failed to load some dashboard data. Endpoints: ${failedEndpoints.join(', ')}`
            );
          }
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        debugError('Unexpected error in fetchSecondaryData:', err);
        setSecondaryError(
          `Failed to load secondary dashboard data: ${err.message || 'Unknown error'}`
        );
      }
    } finally {
      if (isMountedRef.current) {
        setSecondaryLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryString,
    requestKeySuffix,
    isAllTime,
    isMountedRef,
    setCostBreakdown,
    // Note: costBreakdown is intentionally NOT in dependencies to prevent infinite loops
    // It's used inside the function but changes to it shouldn't trigger refetch
    templateGridPage,
    templateGridPageSize,
    templateGridCategory,
    templateGridHideZeroActivity,
    templateGridSearch,
    templateGridSortBy,
    templateGridSortOrder,
  ]);

  return {
    secondaryLoading,
    secondaryError,
    templatePerformance,
    topPerformers,
    categoryAnalytics,
    modelUsageDistribution,
    categoryAnalyticsData,
    adapters,
    executionsPerWeek,
    providerDistribution,
    topModels,
    costComparison,
    fetchSecondaryData,
  };
};
